import React, { useEffect, useState, useRef, useMemo } from 'react';
import cn from 'classnames';

import { ChevronDownV2Icon } from '@app/components/ui/icons/icons-list';
import { OptionItem, OptionsPosition, SelectVariant } from '@app/components/ui/select/select.type';
import useOnClickOutside from '@app/hooks/use-onclick-outside';
import SelectItem from '@app/components/ui/select/select-item';

import { pictogramsData } from '@app/components/metric-table-value-build';

import { IconNameType } from '@app/components/ui/icons/icon-name.type';

import { Input } from '@ui';

import { widgetIconsMap } from '@app/pages/dashboards/blocks/dashboard-widgets/constants/widget-pictograms';

import TooltipWrapper from '../tooltip-wrapper';
import Icon from '../icons';

import {
  optionValueClassName,
  selectOptionsWrapper,
  selectWrapperClassName,
} from './select.styles';

type SelectProps = {
  defaultValue?: string | number | string[] | number[] | undefined;
  isEmpty?: boolean;
  withoutBorder?: boolean;
  smallMX?: boolean;
  withSearch?: boolean;
  size?: number;
  activeOptionIconSize?: number;
  placeholder?: string;
  customClassName?: string;
  title?: string;
  error?: string;
  additionalLabelToEveryValue?: string;
  dropTop?: boolean;
  position?: OptionsPosition;
  variant?: SelectVariant;
  options: Array<OptionItem>;
  onChange?: (value: OptionItem['value']) => void;
  onChangeMultiple?: (value: OptionItem['value'][]) => void;
  dropHeight?: number;
  dropWidth?: number;
  pictogramListSize?: number;
  disable?: boolean;
  isMulti?: boolean;
  withoutPadding?: boolean;
  pictogramsMode?: boolean;
  additionalPictogramToValueLabel?: boolean;
  tooltip?: {
    content: string;
    place?: 'left' | 'right' | 'top' | 'bottom';
    id: string;
  };
  customValueDisplayStyles?: React.CSSProperties;
  pictogramsFrom?: 'widgets' | 'pictograms';
};

const Select = (props: SelectProps) => {
  const {
    defaultValue,
    position = 'left',
    dropTop = false,
    isEmpty,
    options,
    withSearch = false,
    withoutPadding = false,
    size,
    title,
    variant = 'medium',
    customClassName,
    placeholder,
    onChange,
    dropHeight = 180,
    dropWidth = 300,
    disable = false,
    tooltip,
    isMulti = false,
    pictogramListSize,
    smallMX,
    error,
    pictogramsMode,
    additionalLabelToEveryValue,
    onChangeMultiple,
    withoutBorder = false,
    customValueDisplayStyles,
    activeOptionIconSize,
    pictogramsFrom = 'pictograms',
  } = props;
  const pictograms = pictogramsFrom === 'pictograms' ? pictogramsData : widgetIconsMap;
  const [isShowOptions, changeIsShowOptions] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeOption, setActiveOption] = useState<
    OptionItem['value'] | OptionItem['value'][] | undefined
  >(() => {
    if (isMulti) {
      return Array.isArray(defaultValue) ? defaultValue : [];
    }

    return Array.isArray(defaultValue) ? defaultValue[0] : defaultValue;
  });

  const optionsRef = useRef<HTMLDivElement>(null);

  const resizeSelectStyle = useMemo(() => (size ? { width: `${size}px` } : {}), [size]);

  const resizeSelectStyle2 = useMemo(
    () => (size ? { maxWidth: `${size}px`, overflow: 'hidden' } : {}),
    [size],
  );

  const dropdownHeightStyle = useMemo(() => ({ maxHeight: `${dropHeight}px` }), [dropHeight]);

  const preparedOptions = useMemo(() => {
    if (title && defaultValue && options[0]?.title !== title) {
      return [
        {
          title,
          value: Array.isArray(defaultValue) ? defaultValue[0] : defaultValue,
        },
        ...options,
      ];
    }

    return options;
  }, [title, defaultValue, options]);

  const optionsMap = useMemo(() => {
    return new Map(preparedOptions.map((item) => [item.value, item]));
  }, [preparedOptions]);

  const filteredOptions = useMemo(() => {
    if (!withSearch || !searchValue.trim()) {
      return preparedOptions;
    }

    const termLower = searchValue.toLowerCase();

    const fullMatches: OptionItem[] = [];
    const letterMatches: OptionItem[] = [];

    preparedOptions.forEach((option) => {
      const word = String(option.title).toLowerCase();

      if (word.includes(termLower)) {
        fullMatches.push(option);
      } else {
        const wordLetters = word.split('');

        const isLettersMatch = termLower.split('').every((letter) => wordLetters.includes(letter));

        if (isLettersMatch) {
          letterMatches.push(option);
        }
      }
    });

    return [...fullMatches, ...letterMatches];
  }, [preparedOptions, searchValue, withSearch]);

  useEffect(() => {
    if (isMulti) {
      setActiveOption(Array.isArray(defaultValue) ? defaultValue : []);
    } else {
      setActiveOption(Array.isArray(defaultValue) ? defaultValue[0] : defaultValue);
    }
  }, [defaultValue, isMulti]);

  useOnClickOutside(optionsRef, () => changeIsShowOptions(false));

  function handleChangeValue(value: OptionItem['value']) {
    if (isMulti) {
      const currentValues = (activeOption as (string | number)[]) || [];

      let newValuesArray;

      if (currentValues.includes(value)) {
        newValuesArray = currentValues.filter((item) => item !== value);
      } else {
        newValuesArray = [...currentValues, value];
      }

      setActiveOption(newValuesArray);
      onChangeMultiple?.(newValuesArray);
    } else {
      setActiveOption(value);
      onChange?.(value);
    }

    changeIsShowOptions(false);
    // проблема тут , если не мульти , то changeIsShowOptions будто не отрабатывает , после клика опции не скрываются
  }
  function toggleShowOptions(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    const target = event.target as HTMLElement;

    if (target.closest('#deleteSelectedFromMulti')) {
      return;
    }

    changeIsShowOptions((prev) => !prev);
  }

  function handleDeleteSelectedFromMulti(value: string | number) {
    const currentValues = (activeOption as (string | number)[]) || [];

    const newValuesArray = currentValues.filter((item) => item !== value);

    setActiveOption(newValuesArray);
    onChangeMultiple?.(newValuesArray);
  }

  const renderSelectedValue = () => {
    const activeOptionData = optionsMap.get(activeOption as string | number);

    if (typeof activeOptionData?.title !== 'undefined') {
      return (
        <span
          className={cn((activeOptionData.title === '' || activeOption === '') && 'text-b_dark')}
        >
          {pictogramsMode ? (
            <Icon
              size={activeOptionIconSize || 24}
              className="text-action"
              name={pictograms[activeOptionData.title]}
            />
          ) : (
            activeOptionData.title
          )}
        </span>
      );
    }

    return (
      <div className={`text-4color ${!placeholder && 'h-[calc(50%-5px)]'}`}>{placeholder}</div>
    );
  };

  const renderMultiValues = () => {
    const values = Array.isArray(activeOption)
      ? activeOption.filter((val) => (typeof val === 'number' ? true : !!val))
      : [];

    return values.map((option, index) => {
      const optionData = optionsMap.get(option);

      return (
        <span
          className={`${
            withoutPadding ? 'ml-[3px]' : 'mr-[5px]'
          } flex items-center h-full max-w-full`}
          key={String(option)}
        >
          {(index === 0 && additionalLabelToEveryValue) || ''}
          {optionData?.icon && (
            <Icon size={30} className="text-action" name={optionData.icon as IconNameType} />
          )}
          {pictogramsMode ? (
            <Icon size={30} className="text-action" name={pictograms[optionData?.title || '']} />
          ) : (
            <div className="p-[2px_8px_2px_8px] bg-b-light rounded-[4px] hf flex items-start gap-[5px] max-w-full">
              <div className="truncate">{optionData?.title}</div>

              <div
                onClick={() => handleDeleteSelectedFromMulti(option)}
                id="deleteSelectedFromMulti"
                className="-mt-[1px] cursor-pointer shrink-0"
              >
                <Icon name="XIcon" size={7} className="text-3color" />
              </div>
            </div>
          )}
          {index !== values.length - 1 ? (pictogramsMode ? ',' : '') : ''}
        </span>
      );
    });
  };

  const renderValueContent = () => {
    return (
      <div
        style={customValueDisplayStyles}
        className={cn(
          optionValueClassName({ variant, withoutPadding, smallMX: smallMX || false }),
          'flex flex-wrap min-w-0 overflow-hidden',
        )}
      >
        {isMulti && ((activeOption as (string | number)[]) || []).length
          ? renderMultiValues()
          : renderSelectedValue()}
      </div>
    );
  };

  const renderOptions = filteredOptions.map((option) => {
    const activeItem = isMulti
      ? Array.isArray(activeOption) && activeOption.includes(option.value)
      : activeOption === option.value;

    return (
      <SelectItem
        withoutPadding={withoutPadding}
        pictogramsMode={pictogramsMode}
        pictogramsFrom={pictogramsFrom}
        pictogramListSize={pictogramListSize}
        isMulti={isMulti}
        key={String(option.value)}
        icon={option.icon}
        onChange={() => handleChangeValue(option.value)}
        active={activeItem}
        option={option}
      />
    );
  });

  const defaultEmptyOption: OptionItem = {
    title: placeholder || '',
    value: '',
  };

  return (
    <div
      ref={optionsRef}
      className={selectWrapperClassName({
        variant,
        disable,
        error: Boolean(error),
        withoutBorder,
        customClassName: customClassName || '',
      })}
      onClick={disable ? undefined : toggleShowOptions}
      style={resizeSelectStyle}
    >
      <div
        style={resizeSelectStyle2}
        className="flex h-full relative items-center justify-between w-full min-w-0"
      >
        {tooltip ? (
          <TooltipWrapper truncate place={tooltip.place} content={tooltip.content} id={tooltip.id}>
            {renderValueContent()}
          </TooltipWrapper>
        ) : (
          renderValueContent()
        )}

        <div className="w-[18px] text-center items-center flex justify-center shrink-0">
          <ChevronDownV2Icon
            size={13}
            className={cn('text-3color transition mr-[6px]', isShowOptions && 'rotate-180')}
          />
        </div>
      </div>

      {isShowOptions && (
        <div
          className={selectOptionsWrapper({ dropTop, position })}
          style={dropWidth ? { width: `${dropWidth}px`, maxWidth: `${dropWidth}px` } : {}}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={cn(
              'select_scrollbar',
              'max-h-[160px] my-[8px] min-h-[34px] mx-[3px] overflow-hidden overflow-y-auto',
            )}
            style={dropdownHeightStyle}
          >
            <ul
              className={cn(pictogramsMode && 'justify-stretch-stretch flex flex-wrap gap-[3px]')}
            >
              {withSearch && (
                <div className="w-full px-[20px] py-2">
                  <Input
                    defaultValue={searchValue}
                    onChange={(event) => {
                      setSearchValue(event.target.value);
                    }}
                  />
                </div>
              )}

              {isEmpty && (
                <SelectItem
                  active={activeOption === ''}
                  onChange={() => handleChangeValue('')}
                  option={defaultEmptyOption}
                />
              )}

              {renderOptions}
            </ul>
          </div>
        </div>
      )}

      {!!error && (
        <div className="mt-[1px] absolute text-sm text-red-600 dark:text-red-500">{error}</div>
      )}
    </div>
  );
};

export default Select;
